import { client as request } from "./request";
import BankAccount from "@/entities/bank-account";
import { TaskTypeId } from "../entities/ids";
import GroupFilter from "@/entities/group-filter";
import { Stamp } from "./stamp-api";

export const enum CompanyType {
  Corporation = 1,
  Individual = 2,
  EndUser = 3
}

export interface Company {
  companyId: any;
  companyTypeId: CompanyType;
  name: string;
  furigana: string;
  client: boolean;
  vendor: boolean;
  family: boolean;
  office: Office;
  favorite?: boolean;
  isFirstCompany?: boolean;
}

export interface Office {
  companyId: any;
  officeId: any;
  name: string;
  mail: string;
  tel1: string;
  tel2: string;
  fax: string;
  postcode: string;
  address: string;
  latitude: number;
  longitude: number;
  note: string;
  cutoffRule: number;
  monthRule: number;
  dayRule: number;
  bankAccounts: BankAccount[];
  staffList: Staff[];
  stampList: Stamp[] | null;
  profitRate: number | null;
  invoiceNumber?: string;
}

export interface Staff {
  officeId: any;
  office?: Office;
  staffId: any;
  company?: Company;
  name: string;
  tel: string;
  mail: string;
  workTypeIds: TaskTypeId[];
  isHiddenInfo: number;
}

export interface GetCompaniesQuery {
  family?: number;
  category?: string;
  companyType?: string;
}

/**
 * 会社の一覧を取得する
 */
export async function getAllCompanies(
  query: GetCompaniesQuery = {}
): Promise<Company[]> {
  const res = await request.get("get_all_companies.php", query);
  return res.companyList;
}

export async function getOurCompanies() {
  return await getAllCompanies({ family: 1 });
}

export async function getOtherCompanies() {
  return await getAllCompanies({ family: 0 });
}

export async function saveCompany(data: FormData) {
  const res = await request.post("save_company.php", data);
  return res.companyId;
}

export async function updateFavoriteCompany(
  companyId: number,
  favorite: boolean
): Promise<void> {
  const d = new FormData();
  d.append("companyId", String(companyId));
  d.append("favorite", String(Number(favorite || 0)));
  await request.post("save_favorite_company.php", d);
}

export async function savePerson(data: FormData) {
  const res = await request.post("save_private_customer.php", data);
  return res.officeId;
}

export async function savePersonUser(data: FormData) {
  const res = await request.post("save_end_user.php", data);
  return res.companyId;
}

/**
 * @deprecated 使われてない
 */
export async function getOurCompany() {
  const res = await request.get("get_our_company.php");
  return res;
}

/**
 * 会社情報を削除する
 */
export async function deleteCompany(companyId: any) {
  const d = new FormData();
  d.append("companyId", companyId);
  try {
    await request.post("delete_company.php", d);
  } catch (e) {
    alert((e as string[]).join("\n"));
  }
}

/**
 * 店舗情報を保存する
 */
export async function saveOffice(data: FormData) {
  // 使ってない
  const res = await request.post("save_office.php", data);
  return res.officeId;
}

/**
 * 店舗情報を削除する
 */
export async function deleteOffice(officeId: any) {
  const d = new FormData();
  d.append("officeId", officeId);
  await request.post("delete_office.php", d);
}

/**
 * 現場担当の一覧を取得する
 */
export async function getAllManagers(): Promise<Staff[]> {
  try {
    return await request.get("get_all_managers.php");
  } catch (e) {
    return [];
  }
}

export async function getAllSalespeople(): Promise<Staff[]> {
  try {
    return await request.get("get_all_salespeople.php");
  } catch (e) {
    return [];
  }
}

export async function getAllStaffs(
  options: { family?: number; isGetStaffAll?: number } = {}
) {
  const data = await request.get("get_all_staffs.php", {
    ...options
  });
  return data.staffList as Staff[];
}

export async function getOurStaffs() {
  return await getAllStaffs({ family: 1, isGetStaffAll: 1 });
}

export async function getOtherStaffs() {
  return await getAllStaffs({ family: 0, isGetStaffAll: 1 });
}

export async function saveStaff(staff: Staff) {
  const d = new FormData();
  d.append("id", staff.staffId);
  d.append("officeId", staff.officeId);
  d.append("name", staff.name);
  d.append("tel", staff.tel);
  d.append("mail", staff.mail);
  staff.workTypeIds.forEach(id => {
    d.append("workTypeIds[]", String(id));
  });
  await request.post("save_staff.php", d);
}

export async function deleteStaff(id: number | string) {
  const d = new FormData();
  d.append("id", String(id));
  await request.post("delete_staff.php", d);
}

/**
 * 発注先の一覧を取得する
 */
export async function getVendors() {
  const companies = await getOtherCompanies();
  const filter = new GroupFilter({ role: "vendor" });
  return companies.filter(c => filter.isSatisfied(c));
}

/**
 * 発注先の選択肢を取得する
 */
export async function getVendorOptions() {
  return (await getVendors()).map(vendor => ({
    text: vendor.name,
    value: String(vendor.office.officeId)
  }));
}
